import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import "./css/LoginFormModal.css"; // Reuse existing modal styles
import "./css/PhoneVerificationPopup.css"; // Phone verification specific styles
import UserAPI from "../services/UserAPI"; // Import UserAPI for phone verification calls
import SmsConsentAPI from "../services/SmsConsentAPI"; // Import SmsConsentAPI for SMS consent

function PhoneVerificationPopup({
  user,
  onClose,
  onVerificationComplete,
  smsSentByLogin = false,
}) {
  const [verificationCode, setVerificationCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [initialSmsSent, setInitialSmsSent] = useState(smsSentByLogin);
  const [smsConsentChecked, setSmsConsentChecked] = useState(false);

  // Send initial SMS when popup opens (skipped when login flow already sent it)
  useEffect(() => {
    let cancelled = false;

    const sendInitialSms = async () => {
      if (!initialSmsSent && user?.uuid) {
        try {
          console.log(
            "PhoneVerificationPopup: Sending initial SMS for user:",
            user.uuid,
          );
          await UserAPI.sendPhoneVerification(user.uuid);
          if (!cancelled) {
            setInitialSmsSent(true);
            console.log(
              "PhoneVerificationPopup: Initial SMS sent successfully",
            );
          }
        } catch (error) {
          console.error(
            "PhoneVerificationPopup: Failed to send initial SMS:",
            error,
          );
          if (!cancelled) {
            setErrorMessage(
              "Failed to send verification code. Please try resending.",
            );
          }
        }
      }
    };

    sendInitialSms();

    return () => {
      cancelled = true;
    };
  }, [user?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    setVerificationCode(e.target.value);
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!verificationCode.trim()) {
      setErrorMessage("Please enter the verification code.");
      return;
    }

    if (!smsConsentChecked) {
      setErrorMessage("Please check the SMS consent checkbox to continue.");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      console.log(
        "PhoneVerificationPopup: Verifying phone for user:",
        user.uuid,
      );

      const response = await UserAPI.verifyUserPhone(user.uuid, {
        verificationCode: verificationCode,
      });
      console.log(
        "PhoneVerificationPopup: Phone verification successful:",
        response,
      );

      // If SMS consent is checked, create SMS consent record
      if (smsConsentChecked) {
        try {
          console.log(
            "PhoneVerificationPopup: Creating SMS consent for user:",
            user.uuid,
          );
          await SmsConsentAPI.optInSmsConsent(user.uuid);
          console.log(
            "PhoneVerificationPopup: SMS consent created successfully",
          );
        } catch (consentError) {
          console.error(
            "PhoneVerificationPopup: Failed to create SMS consent:",
            consentError,
          );
          // Don't fail the whole process if consent creation fails
        }
      }

      // Show success message and close popup
      toast.success(
        `Phone verification completed successfully! Your account is now fully verified.`,
        { duration: 5000 },
      );
      onVerificationComplete();
      onClose();
    } catch (error) {
      console.error(
        "PhoneVerificationPopup: Phone verification failed:",
        error,
      );

      if (error.response && error.response.status === 400) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          setErrorMessage(errorData);
        } else if (errorData.message) {
          setErrorMessage(errorData.message);
        } else {
          setErrorMessage("Invalid verification code. Please try again.");
        }
      } else if (error.response && error.response.status === 404) {
        setErrorMessage(
          "Verification code not found or expired. Please request a new code.",
        );
      } else {
        setErrorMessage("Verification failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setErrorMessage("");

    try {
      console.log(
        "PhoneVerificationPopup: Resending verification code for user:",
        user.uuid,
      );

      await UserAPI.sendPhoneVerification(user.uuid);
      console.log(
        "PhoneVerificationPopup: Verification code resent successfully",
      );

      toast("A new verification code has been sent to your phone.", {
        duration: 4000,
        icon: "ℹ️",
      });
    } catch (error) {
      console.error(
        "PhoneVerificationPopup: Failed to resend verification code:",
        error,
      );

      if (error.response && error.response.status === 404) {
        setErrorMessage("User not found. Please contact support.");
      } else if (error.response && error.response.status === 500) {
        setErrorMessage(
          "Failed to send verification code. Please try again later.",
        );
      } else {
        setErrorMessage(
          "Failed to resend verification code. Please try again.",
        );
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleSkipForNow = () => {
    const message = `Welcome ${user.firstName} ${user.lastName}! You can verify your phone number later from your account settings.`;

    toast(message, { duration: 5000, icon: "ℹ️" });
    onClose();
  };

  const handleBackdropClick = (e) => {
    // Don't allow closing by clicking outside - require action
    e.stopPropagation();
  };

  return (
    <div
      className="modal phone-verification-modal"
      onClick={handleBackdropClick}
    >
      <div className="modal-content phone-verification-content">
        <h2>Verify Your Phone Number</h2>
        <div className="verification-popup-content">
          <p>
            <strong>Welcome, {user.firstName}!</strong>
          </p>
          <p>
            To complete your account setup, please verify your phone number.
          </p>
          <p>
            We've sent a verification code via SMS to:
            <br />
            <strong>{user.phone}</strong>
          </p>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="verificationCode">Verification Code</label>
              <input
                type="text"
                id="verificationCode"
                name="verificationCode"
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={handleChange}
                maxLength="6"
                pattern="[0-9]{6}"
                style={{
                  borderColor: errorMessage ? "lightcoral" : "#ddd",
                  textAlign: "center",
                  fontSize: "18px",
                  letterSpacing: "2px",
                }}
                disabled={isLoading}
              />
            </div>

            <div className="form-group" style={{ marginTop: "1rem" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  lineHeight: "1.5",
                }}
              >
                <input
                  type="checkbox"
                  checked={smsConsentChecked}
                  onChange={(e) => setSmsConsentChecked(e.target.checked)}
                  style={{
                    marginRight: "0.5rem",
                    marginTop: "0.2rem",
                    width: "18px",
                    height: "18px",
                    cursor: "pointer",
                  }}
                  disabled={isLoading}
                />
                <span>
                  I consent to receive SMS notifications from KinShield for
                  security alerts and important account updates. Standard
                  message and data rates may apply. You can opt out at any time
                  in Settings.
                </span>
              </label>
            </div>

            {errorMessage && (
              <p className="error-message" style={{ color: "lightcoral" }}>
                {errorMessage}
              </p>
            )}

            <button
              type="submit"
              className="login-button"
              disabled={isLoading || !smsConsentChecked}
              style={{
                opacity: !smsConsentChecked || isLoading ? 0.5 : 1,
                cursor:
                  !smsConsentChecked || isLoading ? "not-allowed" : "pointer",
              }}
            >
              {isLoading ? "Verifying..." : "Verify Phone"}
            </button>
          </form>

          <div className="verification-actions">
            <button
              type="button"
              className="link-button"
              onClick={handleResendCode}
              disabled={isResending}
              style={{ margin: "10px 5px" }}
            >
              {isResending ? "Sending..." : "Resend Code"}
            </button>

            <button
              type="button"
              className="link-button secondary"
              onClick={handleSkipForNow}
              style={{ margin: "10px 5px" }}
            >
              Skip for Now
            </button>
          </div>

          <div className="verification-note">
            <p>
              <em>
                Note: If you don't receive the SMS, please check that your phone
                can receive text messages and try again.
              </em>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhoneVerificationPopup;
